import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Pagination } from '@/components/ui/Pagination';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { deleteAllParties, deleteParty, importParties, listParties, partiesExportPath, partiesImportTemplatePath, updateParty, type Party } from '@/api/parties';
import { apiError } from '@/lib/apiError';
import { formatINRSymbol } from '@/lib/format';
import { downloadExcel } from '@/lib/downloadExcel';
import { PartyFormModal } from './PartyFormModal';
import { ExcelImportModal } from '@/components/ui/ExcelImportModal';
import { Download, FileDown, Search, Trash2, Upload } from '@/components/icons';

type Tab = 'customer' | 'lead' | 'vendor' | 'all';

/**
 * `mode="leads"` renders this same list as the dedicated Lead Master page —
 * locked to Lead-type parties, no tab bar, and without the party-wide
 * Add/Delete-All controls (which act on every party, not just leads).
 * Default mode is the full Party Master and is unchanged.
 */
export function PartiesPage({ mode = 'default' }: { mode?: 'default' | 'leads' } = {}) {
  const leadsMode = mode === 'leads';
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const [tab, setTab] = useState<Tab>(leadsMode ? 'lead' : 'customer');
  const [search, setSearch] = useState('');
  const [partyType, setPartyType] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Party | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [convertingLead, setConvertingLead] = useState<Party | null>(null);
  const [newType, setNewType] = useState<'Retailer' | 'Wholesaler' | 'Distributor' | 'MBO & SiS' | 'Corporate & Projects' | 'Architect' | 'Customization'>('Retailer');

  const convertMutation = useMutation({
    mutationFn: () => updateParty(convertingLead!.id, { customerType: newType }),
    onSuccess: () => {
      // Drop the enquiries cache too so the ENQ Review page immediately
      // (a) un-gates the Review button, and (b) surfaces the ex-Lead's open
      // enquiries in the Orders tab (originatedAsLead flag).
      qc.invalidateQueries({ queryKey: ['parties'] });
      qc.invalidateQueries({ queryKey: ['enquiries'] });
      toast.push(`Converted ${convertingLead!.name} → ${newType}`, 'success');
      setConvertingLead(null);
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Convert failed'), 'error');
    },
  });

  const filter = useMemo(
    () => ({ type: leadsMode ? 'lead' : tab, customerType: partyType || undefined, search: search || undefined, page, pageSize }),
    [leadsMode, tab, partyType, search, page, pageSize],
  );

  const query = useQuery({
    queryKey: ['parties', filter],
    queryFn: () => listParties(filter),
    placeholderData: keepPreviousData,
  });

  const delMutation = useMutation({
    mutationFn: deleteParty,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parties'] });
      toast.push('Party deleted', 'info');
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Delete failed'), 'error');
    },
  });

  const delAllMutation = useMutation({
    mutationFn: deleteAllParties,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['parties'] });
      toast.push(`Deleted ${res.deleted} parties`, 'info');
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Delete failed'), 'error');
    },
  });

  async function onDeleteAll() {
    if (!await confirmDlg(`Delete ALL ${total} parties? This cannot be undone.`)) return;
    delAllMutation.mutate();
  }

  function openAdd() {
    setEditing(null);
    setModalOpen(true);
  }
  function openEdit(p: Party) {
    setEditing(p);
    setModalOpen(true);
  }
  async function onDelete(p: Party) {
    if (!await confirmDlg(`Delete party "${p.name}"?`)) return;
    delMutation.mutate(p.id);
  }

  const data = query.data?.data ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div>
      <SectionHeader
        title={leadsMode ? 'Lead Master' : 'Party Master'}      />

      {/* Tabs (Party Master only — the Lead Master page is single-purpose) */}
      {!leadsMode && (
      <div className="mb-2 flex w-fit gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-1">
        {(
          [
            { id: 'customer', label: 'Customers' },
            { id: 'vendor', label: 'Vendors' },
            { id: 'all', label: 'All' },
          ] as { id: Tab; label: string }[]
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => {
              setTab(t.id);
              setPage(1);
            }}
            className={clsx(
              'rounded-md px-4 py-1.5 text-sm font-bold transition',
              tab === t.id
                ? 'bg-gradient-to-br from-accent to-[#e07b00] text-white'
                : 'text-[var(--text-secondary)] hover:text-accent',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      )}

      <FilterBar>
        <FilterField label="Search" className="col-span-3 sm:col-auto">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search"
              className="!pl-5 sm:!pl-8 sm:min-w-[280px]"
              aria-label="Search parties"
            />
          </div>
        </FilterField>
        <FilterField label="Type">
          <Select value={partyType} onChange={(e) => { setPartyType(e.target.value); setPage(1); }} aria-label="Filter by party type">
            <option value="">All Types</option>
            <option value="Retailer">Retailer</option>
            <option value="Wholesaler">Wholesaler</option>
            <option value="Distributor">Distributor</option>
            <option value="Corporate & Projects">Corporate &amp; Projects</option>
            <option value="Architect">Architect</option>
            <option value="Customization">Customization</option>
            <option value="MBO & SiS">MBO &amp; SiS</option>
            <option value="Lead">Lead</option>
            <option value="Vendor">Vendor</option>
          </Select>
        </FilterField>
        {/* Merged chip strip: visible on both viewports, right-aligned inside the filter block. */}
        <div className="col-span-3 flex flex-nowrap gap-1.5 [&>*]:flex-auto [&>*]:whitespace-nowrap sm:col-span-1 sm:ml-auto sm:items-end sm:self-end sm:[&>*]:flex-none">
          <button
            type="button"
            onClick={() => downloadExcel(partiesExportPath(filter), 'GRHAMOY_Parties.xlsx')}
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-success hover:text-success sm:h-8 sm:px-3 sm:text-xs"
          >
            <Download size={12} /> Export
          </button>
          <button
            type="button"
            onClick={() => downloadExcel(partiesImportTemplatePath(), 'GRHAMOY_Parties_Template.xlsx')}
            title="Download a sample Excel with the correct columns and one example row"
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:px-3 sm:text-xs"
          >
            <FileDown size={12} /> Template
          </button>
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:px-3 sm:text-xs"
          >
            <Upload size={12} /> Import
          </button>
          {/* Delete All / Add Party act on ALL party types — omit them on the
              single-purpose Lead Master page to avoid nuking non-lead parties. */}
          {!leadsMode && (
          <button
            type="button"
            onClick={onDeleteAll}
            disabled={delAllMutation.isPending || total === 0}
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-danger shadow-sm transition hover:border-danger hover:text-danger disabled:opacity-50 sm:h-8 sm:px-3 sm:text-xs"
          >
            {delAllMutation.isPending ? (
              'Deleting…'
            ) : (
              <span className="inline-flex items-center gap-1"><Trash2 size={12} /> Delete All</span>
            )}
          </button>
          )}
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex h-7 items-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:h-8 sm:px-3 sm:text-xs"
          >
            {leadsMode ? '＋ Add Lead' : '＋ Add Party'}
          </button>
        </div>
      </FilterBar>

      <Table<Party>
        storageKey="parties"
        title={
          tab === 'customer' ? 'Customers'
            : tab === 'lead' ? 'Leads'
            : tab === 'vendor' ? 'Vendors'
            : 'All Parties'
        }
        count={`Showing ${data.length} of ${total}`}
        rows={data}
        rowKey={(r) => r.id}
        loading={query.isFetching && data.length === 0}
        columns={[
          { key: 'code', header: 'Code', cell: (r) => <span className="text-xs">{r.code}</span> },
          { key: 'name', header: 'Name', cell: (r) => <span className="font-bold">{r.name}</span> },
          {
            key: 'type',
            header: 'Type',
            cell: (r) => {
              if (r.isCustomer) return r.customerType ?? 'Customer';
              if (r.isSupplier) return 'Vendor';
              if (r.isContractor) return 'Contractor';
              if (r.isIncomeExpense) return 'Income / Expense';
              return '—';
            },
          },
          { key: 'city', header: 'City', cell: (r) => r.city ?? '—' },
          { key: 'state', header: 'State', cell: (r) => r.state?.name ?? r.stateCode ?? '—' },
          { key: 'gstin', header: 'GSTIN', cell: (r) => r.gstin ?? '—' },
          { key: 'mobile', header: 'Mobile', cell: (r) => r.mobile ?? '—' },
          ...(tab !== 'vendor' ? [{
            key: 'ffDiscount',
            header: 'F&F Discount %',
            align: 'right' as const,
            cell: (r: Party) =>
              r.friendsFamilyDiscount != null ? (
                <span className="font-mono text-accent">{parseFloat(r.friendsFamilyDiscount).toFixed(2)} %</span>
              ) : (
                <span className="text-[var(--text-muted)]">—</span>
              ),
          }] : []),
          {
            key: 'credit',
            header: 'Credit Limit',
            cell: (r) => formatINRSymbol(r.creditLimit ?? 0),
            align: 'right',
          },
          {
            key: 'outstanding',
            header: 'Outstanding',
            cell: (r) => formatINRSymbol(r.outstanding),
            align: 'right',
          },
          {
            key: 'actions',
            header: '',
            width: '190px',
            cell: (r) => (
              <div className="flex flex-nowrap items-center gap-1.5">
                <Button variant="glass" size="sm" onClick={() => openEdit(r)}>Edit</Button>
                {r.customerType === 'Lead' && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => { setNewType('Retailer'); setConvertingLead(r); }}
                    title="Change Lead → Customer to unblock enquiry pipeline"
                  >
                    Convert
                  </Button>
                )}
                <Button variant="glass" size="sm" onClick={() => onDelete(r)} title="Delete party"><Trash2 size={14} /></Button>
              </div>
            ),
          },
        ]}
        footer={<Pagination page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />}
      />

      <PartyFormModal open={modalOpen} editing={editing} onClose={() => setModalOpen(false)} leadMode={leadsMode} />

      {convertingLead && (
        <div
          className="fixed inset-0 z-[500] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.65)' }}
        >
          <div
            className="w-full max-w-sm overflow-hidden rounded-2xl shadow-2xl"
            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
          >
            <div
              className="border-b px-5 py-3"
              style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}
            >
              <div className="text-sm font-extrabold" style={{ color: 'var(--text-primary)' }}>
                Convert to Customer
              </div>
              <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                {convertingLead.name}
              </div>
            </div>
            <div className="flex flex-col gap-3 px-5 py-4">
              <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                Pick the customer type. This unblocks the enquiry pipeline for this party.
              </div>
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value as typeof newType)}
                className="rounded-lg border border-[var(--border-color)] bg-[var(--input-bg)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-accent"
              >
                <option value="Retailer">Retailer</option>
                <option value="Wholesaler">Wholesaler</option>
                <option value="Distributor">Distributor</option>
                <option value="MBO & SiS">MBO & SiS</option>
                <option value="Corporate & Projects">Corporate & Projects</option>
                <option value="Architect">Architect</option>
                <option value="Customization">Customization</option>
              </select>
            </div>
            <div
              className="flex justify-end gap-2 border-t px-5 py-3"
              style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}
            >
              <Button
                variant="glass"
                size="sm"
                onClick={() => setConvertingLead(null)}
                disabled={convertMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => convertMutation.mutate()}
                disabled={convertMutation.isPending}
              >
                {convertMutation.isPending ? 'Converting…' : 'Convert'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ExcelImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Parties"
        expectedColumns={['Code', 'Name*', 'Customer*', 'Vendor*', 'Customer Type', 'GSTIN', 'Mobile', 'Email', 'Contact Person', 'Address', 'City', 'State', 'State Code', 'PIN', 'Opening Balance', 'Payment Terms']}
        onImport={importParties}
        onSuccess={() => qc.invalidateQueries({ queryKey: ['parties'] })}
      />
    </div>
  );
}
